<template>
  <nav class="navbar" :class="{ 'mobile': isMobile }">
    <div class="navbar-container">
      <!-- Logo -->
      <router-link :to="localePath('/')" class="navbar-logo">
        <img src="/logo.svg" alt="Bentana" class="logo-img" />
        <span v-if="!isMobile" class="logo-text">Bentana</span>
      </router-link>

      <!-- 桌面版導航連結 -->
      <div v-if="!isMobile" class="navbar-links">
        <router-link :to="localePath('/')" class="nav-link">{{ $t('nav.home') }}</router-link>
        <router-link :to="localePath('/invest')" class="nav-link">{{ $t('nav.invest') }}</router-link>
        <router-link :to="localePath('/dashboard')" class="nav-link">{{ $t('nav.dashboard') }}</router-link>
        <router-link :to="localePath('/whitepaper')" class="nav-link">{{ $t('nav.whitepaper') }}</router-link>
      </div>

      <!-- 手機版漢堡菜單按鈕 -->
      <button 
        v-if="isMobile" 
        @click="toggleMobileMenu"
        class="mobile-menu-btn"
        :class="{ 'active': mobileMenuOpen }"
        :aria-label="$t('nav.menu')"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- 右側操作 -->
      <div class="navbar-actions">
        <!-- 語言切換（桌面版） -->
        <LanguageSwitcher v-if="!isMobile" />
        
        <!-- 錢包連接 -->
        <div class="navbar-wallet">
          <WalletConnect :compact="isMobile" />
        </div>
      </div>
    </div>

    <!-- 手機版菜單 -->
    <transition name="slide-down">
      <div v-if="isMobile && mobileMenuOpen" class="mobile-menu">
        <a 
          href="javascript:void(0)"
          class="mobile-menu-link"
          @click="handleMobileNav('/')"
        >
          {{ $t('nav.home') }}
        </a>
        <a 
          href="javascript:void(0)"
          class="mobile-menu-link"
          @click="handleMobileNav('/invest')"
        >
          {{ $t('nav.invest') }}
        </a>
        <a 
          href="javascript:void(0)"
          class="mobile-menu-link"
          @click="handleMobileNav('/dashboard')"
        >
          {{ $t('nav.dashboard') }}
        </a>
        <a 
          href="javascript:void(0)"
          class="mobile-menu-link"
          @click="handleMobileNav('/whitepaper')"
        >
          {{ $t('nav.whitepaper') }}
        </a>
        <div class="mobile-menu-lang">
          <LanguageSwitcher />
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDevice } from '@/composables/useDevice'
import { useI18nRouter } from '@/composables/useI18nRouter'
import LanguageSwitcher from './LanguageSwitcher.vue'
import WalletConnect from './WalletConnect.vue'

const { isMobile } = useDevice()
const mobileMenuOpen = ref(false)
const route = useRoute()
const router = useRouter()
const { localePath, localePush } = useI18nRouter()

// 移除所有調試日誌

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}

// 處理移動端導航點擊
const handleMobileNav = (path) => {
  closeMobileMenu()
  // 使用 localePush 進行導航，確保路由正確
  localePush(path).catch(err => {
    console.error('導航失敗:', err)
  })
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: var(--border-width) solid var(--border-color);
  height: 80px;
  transition: all var(--transition-base);
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.navbar-logo {
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.navbar-logo:hover {
  opacity: 0.8;
}

.logo-img {
  width: 40px;
  height: 40px;
}

.logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3B82F6 0%, #06B6D4 50%, #7C3AED 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.navbar-links {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-link {
  color: var(--text-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
}

.nav-link:hover {
  color: var(--color-primary);
}

.nav-link.router-link-active {
  color: var(--color-primary);
}

.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #3B82F6, #06B6D4);
  border-radius: 2px;
}

.navbar-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.navbar-wallet {
  display: flex;
  align-items: center;
}

.wallet-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.wallet-btn.connect {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-hover) 100%);
  color: white;
  box-shadow: var(--shadow-primary);
  transition: all var(--transition-base);
}

.wallet-btn.connect:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-primary-hover);
  background: linear-gradient(135deg, var(--color-primary-hover) 0%, var(--color-primary) 100%);
}

.wallet-connected {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.75rem;
}

.wallet-address {
  font-family: 'Courier New', monospace;
  color: var(--color-primary);
  font-weight: 600;
}

.wallet-btn.disconnect {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(156, 163, 175, 0.3);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.wallet-btn.disconnect:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.5);
  color: #ef4444;
}

/* 手機版樣式 */
@media (max-width: 768px) {
  .navbar {
    height: 60px;
  }

  .navbar-container {
    padding: 0 1rem;
  }

  .logo-text {
    display: none; /* 手機版隱藏文字 */
  }

  .logo-img {
    width: 32px;
    height: 32px;
  }

  .navbar-actions {
    gap: 0.5rem;
  }

  .wallet-btn.mobile {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  .wallet-connected.mobile {
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
  }

  .wallet-address {
    font-size: 0.75rem;
  }

  .mobile-menu-btn {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    z-index: var(--z-fixed);
  }

  .mobile-menu-btn span {
    width: 24px;
    height: 2px;
    background: var(--color-text-primary);
    transition: all 0.3s ease;
    border-radius: 2px;
  }

  .mobile-menu-btn.active span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }

  .mobile-menu-btn.active span:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-btn.active span:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -6px);
  }

  .mobile-menu {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    background: var(--bg-card);
    backdrop-filter: blur(20px) saturate(180%);
    border-top: var(--border-width) solid var(--border-color);
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    box-shadow: var(--shadow-lg);
    z-index: 9998;
    max-height: calc(100vh - 60px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .mobile-menu-link {
    padding: 1rem;
    color: var(--color-text-primary);
    text-decoration: none;
    border-radius: var(--radius-md);
    transition: all var(--transition-base);
    font-weight: 500;
    display: flex;
    align-items: center;
    cursor: pointer;
    -webkit-tap-highlight-color: rgba(59, 130, 246, 0.2);
    touch-action: manipulation;
    user-select: none;
    min-height: 48px;
    width: 100%;
    position: relative;
    z-index: 9999;
  }

  .mobile-menu-link:active {
    background: rgba(59, 130, 246, 0.15);
    transform: scale(0.98);
  }

  .mobile-menu-link:hover {
    background: rgba(59, 130, 246, 0.1);
    color: var(--color-primary);
  }

  .mobile-menu-lang {
    margin-top: 0.5rem;
    padding-top: 1rem;
    border-top: var(--border-width) solid var(--border-color);
  }
}

/* 動畫 */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

